"use client";

import { c as _c } from "react/compiler-runtime";
import posthog from "posthog-js";
import { ReactNode, createContext, use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaPhone, FaRegEnvelope } from "react-icons/fa6";
import { useParams } from "next/navigation";
import { AuthFlow, AuthMethodId, AuthSuccess, AuthSurface, GoogleSignUpContinuation, PendingGoogleSignUp, PhoneAuthStep, PortalSignUp, useAuthFlow, useGoogleSignUpContinuation, usePhoneAuth } from "@/components/auth";
import { recordCompletedSignUp, recordPatientAccess } from "@/components/auth/patient-sign-in-telemetry";
import { signUpSourceForSurface } from "@/components/auth/sign-up/sign-up-attribution";
import { patientSourceForSurface } from "@/components/auth/sign-up/sign-up-source";
import { Button } from "@/components/ui";
import { useSelfSchedulePatientDetails } from "@/contexts/self-schedule-patient-details-context";
import { useSelfScheduleAuthSuccess } from "@/hooks/auth/use-self-schedule-auth-success";
import { SelfSchedulePatient } from "@/types/self-schedule";
export interface MfaAuthProps {
  /**
   * Patient-facing surface this MFA shell is mounted on. Threads through
   * to `AuthFlow` so the `patient_auth_resolved` PostHog event captures
   * which flow the patient signed in from (chat widget, pay, forms, etc.).
   */
  surface: AuthSurface;
  /**
   * Fires when the auth flow resolves to a Patient — either via phone +
   * SMS or via patient-portal credentials. Callers that gate UI on
   * `usePatientClaim` won't usually need this; it's primarily for
   * dialog-style surfaces that need to close themselves.
   */
  onPatientVerified?: (patient: SelfSchedulePatient) => void;
  /**
   * When true, accept portal-account-holding patients via phone OTP
   * instead of redirecting them to the portal-login column. Use on
   * surfaces that suppress the portal column (kiosks like check-in)
   * where redirecting users with no alternative login would lock them
   * out.
   */
  allowPortalAccountHolders?: boolean;
  children: ReactNode;
}
interface MfaSignUpContextValue {
  pending: PendingGoogleSignUp | null;
  signingUp: boolean;
  startSignUp: () => void;
  cancel: () => void;
  onSuccess: (credential: AuthSuccess) => Promise<void>;
}
const MfaSignUpContext = createContext<MfaSignUpContextValue | null>(null);

/**
 * Provides AuthFlow + lifted phone-auth context for the multi-method
 * auth UI (portal + phone). Compose around `<MfaAuthPanel>`; pages that
 * want the standard auth-page chrome should wrap with `<AuthShell>`.
 */
export function MfaAuth(t0) {
  const $ = _c(37);
  const {
    surface,
    onPatientVerified,
    allowPortalAccountHolders,
    children
  } = t0;
  const {
    pending,
    continueSignUp,
    cancel: cancelGoogle
  } = useGoogleSignUpContinuation();
  const [signingUp, setSigningUp] = useState(false);
  const patientDetails = useSelfSchedulePatientDetails();
  const active = useRef(true);
  const resolved = useRef(false);
  const generation = useRef(0);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      active.current = true;
      return () => {
        active.current = false;
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== cancelGoogle) {
    t3 = () => {
      generation.current = generation.current + 1;
      cancelGoogle();
      setSigningUp(false);
    };
    $[2] = cancelGoogle;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const cancel = t3;
  let t4;
  if ($[4] !== cancel || $[5] !== onPatientVerified || $[6] !== patientDetails || $[7] !== surface) {
    t4 = patient => {
      if (!active.current || resolved.current) {
        return;
      }
      resolved.current = true;
      cancel();
      const continueAfterAuth = () => {
        if (active.current) {
          onPatientVerified?.(patient);
        }
      };
      if (surface === AuthSurface.SELF_SCHEDULE && patientDetails) {
        patientDetails.afterDetails(patient.id, continueAfterAuth);
      } else {
        continueAfterAuth();
      }
    };
    $[4] = cancel;
    $[5] = onPatientVerified;
    $[6] = patientDetails;
    $[7] = surface;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const onResolved = t4;
  let t5;
  if ($[9] !== pending || $[10] !== signingUp || $[11] !== surface) {
    t5 = credential => {
      if (pending || signingUp) {
        recordCompletedSignUp(AuthSurface.PATIENT_SIGN_UP, signUpSourceForSurface(surface), credential.patientType, credential.kind);
      } else {
        recordPatientAccess(surface, credential.kind, credential.patientType);
      }
    };
    $[9] = pending;
    $[10] = signingUp;
    $[11] = surface;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const onAccessConfirmed = t5;
  let t6;
  if ($[13] !== continueSignUp || $[14] !== onAccessConfirmed || $[15] !== onResolved) {
    t6 = {
      onResolved,
      onUnlinkedPatient: continueSignUp,
      onAccessConfirmed
    };
    $[13] = continueSignUp;
    $[14] = onAccessConfirmed;
    $[15] = onResolved;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const resolveSuccess = useSelfScheduleAuthSuccess(t6);
  let t7;
  if ($[17] !== resolveSuccess) {
    t7 = credential_0 => {
      const attempt = generation.current;
      return resolveSuccess(credential_0, () => active.current && generation.current === attempt && !resolved.current);
    };
    $[17] = resolveSuccess;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const onSuccess = t7;
  let t8;
  if ($[19] !== cancelGoogle) {
    t8 = () => {
      generation.current = generation.current + 1;
      cancelGoogle();
      setSigningUp(true);
    };
    $[19] = cancelGoogle;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== cancel || $[22] !== onSuccess || $[23] !== pending || $[24] !== signingUp || $[25] !== t8) {
    t9 = {
      pending,
      signingUp,
      startSignUp: t8,
      cancel,
      onSuccess
    };
    $[21] = cancel;
    $[22] = onSuccess;
    $[23] = pending;
    $[24] = signingUp;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const signUp = t9;
  const t10 = allowPortalAccountHolders ? AuthMethodId.PHONE : AuthMethodId.PASSWORD;
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [AuthMethodId.PASSWORD, AuthMethodId.PHONE, AuthMethodId.GOOGLE];
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== children || $[29] !== signUp) {
    t12 = <MfaSignUpContext.Provider value={signUp}>{children}</MfaSignUpContext.Provider>;
    $[28] = children;
    $[29] = signUp;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== allowPortalAccountHolders || $[32] !== onSuccess || $[33] !== surface || $[34] !== t10 || $[35] !== t12) {
    t13 = <AuthFlow surface={surface} defaultMethod={t10} enabledMethods={t11} onSuccess={onSuccess} allowPortalAccountHolders={allowPortalAccountHolders}>{t12}</AuthFlow>;
    $[31] = allowPortalAccountHolders;
    $[32] = onSuccess;
    $[33] = surface;
    $[34] = t10;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}
export interface MfaPhoneAuthPanelProps {
  /**
   * Trailing text for the T&S disclosure under the phone form, e.g.
   * "paying your balance".
   */
  tAndSFooterText: string;
  /** Auth-panel heading. Defaults to the active method's standard heading. */
  title?: ReactNode;
  /** Phone-method subheading. */
  description?: ReactNode;
}
export type MfaAuthPanelProps = MfaPhoneAuthPanelProps;

/**
 * Renders the multi-method auth UI with patient-portal login as the primary
 * path and phone sign-in as an alternate method above Google.
 * Must live inside `<MfaAuth>`.
 */
export function MfaAuthPanel(props) {
  const $ = _c(13);
  const {
    state,
    meta
  } = useAuthFlow();
  const signUp = use(MfaSignUpContext);
  if (signUp?.pending) {
    let t0;
    if ($[0] !== meta.surface || $[1] !== signUp.cancel || $[2] !== signUp.onSuccess || $[3] !== signUp.pending) {
      t0 = <GoogleSignUpContinuation pending={signUp.pending} originSurface={meta.surface} onSuccess={signUp.onSuccess} onCancel={signUp.cancel} />;
      $[0] = meta.surface;
      $[1] = signUp.cancel;
      $[2] = signUp.onSuccess;
      $[3] = signUp.pending;
      $[4] = t0;
    } else {
      t0 = $[4];
    }
    return t0;
  }
  if (signUp?.signingUp) {
    let t0;
    if ($[5] !== meta.surface || $[6] !== signUp.cancel || $[7] !== signUp.onSuccess) {
      t0 = <InlinePortalSignUp originSurface={meta.surface} onSuccess={signUp.onSuccess} onCancel={signUp.cancel} />;
      $[5] = meta.surface;
      $[6] = signUp.cancel;
      $[7] = signUp.onSuccess;
      $[8] = t0;
    } else {
      t0 = $[8];
    }
    return t0;
  }
  if (state.activeMethod === AuthMethodId.PHONE) {
    let t0;
    if ($[9] !== props) {
      t0 = <PhoneMfaPanel {...props} />;
      $[9] = props;
      $[10] = t0;
    } else {
      t0 = $[10];
    }
    return t0;
  }
  let t0;
  if ($[11] !== props.title) {
    t0 = <PortalFirstPanel title={props.title} />;
    $[11] = props.title;
    $[12] = t0;
  } else {
    t0 = $[12];
  }
  return t0;
}
function InlinePortalSignUp(t0) {
  const $ = _c(15);
  const {
    originSurface,
    onSuccess,
    onCancel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [AuthMethodId.PHONE];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== originSurface) {
    t2 = patientSourceForSurface(originSurface);
    $[1] = originSurface;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AuthFlow.Header title="Create Account" showOnlyWhilePhoneStep={PhoneAuthStep.ENTER_PHONE} />;
    t4 = <PortalSignUp.EntryForm />;
    t5 = <PortalSignUp.GoogleOption />;
    t6 = <PortalSignUp.Steps />;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] !== onCancel) {
    t7 = <AuthFlow.Phone className="p-0 w-full">{t3}{t4}{t5}{t6}<SignUpBackButton onCancel={onCancel} /></AuthFlow.Phone>;
    $[7] = onCancel;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t2 || $[10] !== t7) {
    t8 = <PortalSignUp.Provider patientSource={t2}>{t7}</PortalSignUp.Provider>;
    $[9] = t2;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== onSuccess || $[13] !== t8) {
    t9 = <AuthFlow surface={AuthSurface.PATIENT_SIGN_UP} defaultMethod={AuthMethodId.PHONE} enabledMethods={t1} onSuccess={onSuccess}>{t8}</AuthFlow>;
    $[12] = onSuccess;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}
function SignUpBackButton(t0) {
  const $ = _c(3);
  const {
    onCancel
  } = t0;
  const {
    state
  } = usePhoneAuth();
  let t1;
  if ($[0] !== onCancel || $[1] !== state.isClaiming) {
    t1 = <Button type="button" variant="ghost" className="mt-4 w-full" onClick={onCancel} disabled={state.isClaiming}>Back to login</Button>;
    $[0] = onCancel;
    $[1] = state.isClaiming;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function PhoneMfaPanel(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <AuthFlow.Phone.Provider><PhoneMfaPanelInner {...props} /></AuthFlow.Phone.Provider>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function PhoneMfaPanelInner(props) {
  const $ = _c(3);
  const {
    state
  } = usePhoneAuth();
  if (state.step === PhoneAuthStep.PORTAL_REQUIRED) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <PortalRequiredPanel />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] !== props) {
    t0 = <AuthFlow.Phone className="p-0 w-full"><PhoneSignInPanel {...props} /></AuthFlow.Phone>;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
export function MfaPhoneAuthPanel(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <AuthFlow.Phone.Provider><AuthFlow.Phone className="p-0 w-full"><PhoneColumn {...props} /></AuthFlow.Phone></AuthFlow.Phone.Provider>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function usePortalAuthContext() {
  const $ = _c(5);
  const {
    tenant
  } = useParams();
  const {
    meta
  } = useAuthFlow();
  const signUp = use(MfaSignUpContext);
  const t0 = tenant ?? "";
  const t1 = signUp?.startSignUp;
  let t2;
  if ($[0] !== meta.onSuccess || $[1] !== meta.surface || $[2] !== t0 || $[3] !== t1) {
    t2 = {
      tenant: t0,
      onSignUp: t1,
      onSuccess: meta.onSuccess,
      surface: meta.surface
    };
    $[0] = meta.onSuccess;
    $[1] = meta.surface;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function PortalHeader(t0) {
  const $ = _c(5);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    title,
    description
  } = t1;
  const t2 = title ?? "Patient Portal Login";
  const t3 = description ?? "Use your portal email and password";
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = <AuthFlow.Header className="mb-4" title={t2} description={t3} />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function PortalLoginPanel(t0) {
  const $ = _c(11);
  const {
    showSignUp: t1,
    onSuccessOverride
  } = t0;
  const showSignUp = t1 === undefined ? true : t1;
  const {
    tenant,
    onSignUp,
    onSuccess,
    surface
  } = usePortalAuthContext();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [AuthMethodId.PASSWORD, AuthMethodId.GOOGLE];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = onSuccessOverride ?? onSuccess;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <PortalHeader description="Sign in the same way you created your portal account" />;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = showSignUp ? onSignUp : undefined;
  let t6;
  if ($[2] !== t5 || $[3] !== tenant) {
    t6 = <AuthFlow.Password resetPasswordTenant={tenant} onSignUp={t5} />;
    $[2] = t5;
    $[3] = tenant;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <AuthFlow.Divider />;
    t8 = <AuthFlow.Google />;
    $[5] = t7;
    $[6] = t8;
  } else {
    t7 = $[5];
    t8 = $[6];
  }
  let t9;
  if ($[7] !== surface || $[8] !== t3 || $[9] !== t6) {
    t9 = <div className="flex flex-col gap-4"><AuthFlow surface={surface} defaultMethod={AuthMethodId.PASSWORD} enabledMethods={t2} onSuccess={t3}>{t4}{t6}{t7}{t8}</AuthFlow></div>;
    $[7] = surface;
    $[8] = t3;
    $[9] = t6;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  return t9;
}
function PortalRequiredPanel() {
  const $ = _c(5);
  const {
    onSuccess,
    surface
  } = usePortalAuthContext();
  let t0;
  if ($[0] !== onSuccess || $[1] !== surface) {
    t0 = credential => {
      posthog.capture("patient_portal_login_handoff_completed", {
        surface
      });
      return onSuccess(credential);
    };
    $[0] = onSuccess;
    $[1] = surface;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const handleSuccess = t0;
  let t1;
  if ($[3] !== handleSuccess) {
    t1 = <PortalLoginPanel showSignUp={false} onSuccessOverride={handleSuccess} />;
    $[3] = handleSuccess;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function PortalFirstPanel(t0) {
  const $ = _c(11);
  const {
    title
  } = t0;
  const {
    actions
  } = useAuthFlow();
  const {
    tenant,
    onSignUp
  } = usePortalAuthContext();
  let t1;
  if ($[0] !== title) {
    t1 = <PortalHeader title={title} />;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onSignUp || $[3] !== tenant) {
    t2 = <AuthFlow.Password resetPasswordTenant={tenant} onSignUp={onSignUp} />;
    $[2] = onSignUp;
    $[3] = tenant;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== actions) {
    t3 = <PortalAlternativeActions onPhoneSignIn={() => actions.setActiveMethod(AuthMethodId.PHONE)} />;
    $[5] = actions;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <div className="mx-auto flex max-w-md flex-col gap-4">{t1}{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function PortalAlternativeActions(t0) {
  const $ = _c(7);
  const {
    onPhoneSignIn
  } = t0;
  const {
    state
  } = useAuthFlow();
  if (state.isInSubFlow) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AuthFlow.Divider />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FaPhone className="h-5 w-5" aria-hidden={true} />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onPhoneSignIn) {
    t3 = <Button type="button" variant="primary" className="w-full" icon={t2} onClick={onPhoneSignIn}>Sign in with phone number</Button>;
    $[2] = onPhoneSignIn;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <AuthFlow.Google />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3) {
    t5 = <div className="space-y-4">{t1}{t3}{t4}</div>;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function PhoneSignInPanel(props) {
  const $ = _c(8);
  const {
    actions
  } = useAuthFlow();
  const {
    state
  } = usePhoneAuth();
  let t0;
  if ($[0] !== props) {
    t0 = <PhoneColumn {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== actions || $[3] !== state.step) {
    t1 = state.step === PhoneAuthStep.ENTER_PHONE && <PhoneAlternativeActions onPortalSignIn={() => actions.setActiveMethod(AuthMethodId.PASSWORD)} />;
    $[2] = actions;
    $[3] = state.step;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = <div className="flex flex-col gap-4">{t0}{t1}</div>;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function PhoneAlternativeActions(t0) {
  const $ = _c(7);
  const {
    onPortalSignIn
  } = t0;
  const {
    state
  } = useAuthFlow();
  if (state.isInSubFlow) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AuthFlow.Divider />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FaRegEnvelope className="h-5 w-5" aria-hidden={true} />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onPortalSignIn) {
    t3 = <Button type="button" variant="primary" className="w-full" icon={t2} onClick={onPortalSignIn}>Sign in with email and password</Button>;
    $[2] = onPortalSignIn;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <AuthFlow.Google />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3) {
    t5 = <div className="space-y-4">{t1}{t3}{t4}</div>;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function PhoneColumn(t0) {
  const $ = _c(12);
  const {
    tAndSFooterText,
    title,
    description
  } = t0;
  const t1 = title ?? "Use Your Phone Number";
  const t2 = description ?? "We'll text you a code to verify it's you.";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <AuthFlow.Header showOnlyWhilePhoneStep={PhoneAuthStep.ENTER_PHONE} title={t1} description={t2} />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <AuthFlow.Phone.Entry className="space-y-6" />;
    t5 = <AuthFlow.Phone.Code className="space-y-6" />;
    t6 = <AuthFlow.Phone.PickPatient className="space-y-6" />;
    t7 = <AuthFlow.Phone.Name className="space-y-6" />;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
  }
  let t8;
  if ($[7] !== tAndSFooterText) {
    t8 = <AuthFlow.Phone.TAndS byText={tAndSFooterText} />;
    $[7] = tAndSFooterText;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t3 || $[10] !== t8) {
    t9 = <>{t3}{t4}{t5}{t6}{t7}{t8}</>;
    $[9] = t3;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
}